import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { byTaskRecency, type TaskRow } from '../lib/fleet';
import { taskWorkWord } from '../lib/fleet-work';
import { orgHref } from '../lib/org';
import { ago } from '../lib/time';
import { Panel } from './Panel';
import { StatusPill } from './StatusPill';
import { TASK_STATUS_TONE } from './TaskQueueItem';

// The Fleet page's "Work in progress" (#6788, prototype `workAndAccounts`): the two
// counts, the five newest tasks and "See all work" into the full board at
// `/fleet/work`, which keeps every control the queue has (New task, Stop, Details).
// This is a HEAD, read-only: a reader who wants to act is one click away.
//
// THE COUNTS ARE THE TILES' (#6559). Working is the machines whose verdict is
// `working`, the Busy tile's number; Waiting is the Waiting in line tile's queue
// depth. Both come from the container's one `fleetTiles` fold over the utilization
// read, so this band and the tiles above it can never disagree. `null` (the read is
// not in) shows "-", never a 0 that claims an idle fleet.
//
// A ROW is title, repo, machine name, work word and time (`taskWorkWord`, the
// board's own word). The machine is the row's `runner_id` named off the
// utilization boxes; a task no machine has taken shows "-".

/** Rows in the summary: the five newest by `byTaskRecency`, any status. */
const SUMMARY_ROWS = 5;

export type WorkInProgressSummaryProps = {
  /** The account's queue rows (`GET /v1/tasks`), in any order. */
  readonly rows: () => readonly TaskRow[];
  /** The Busy tile's Working count, or null before the utilization read is in. */
  readonly working: () => number | null;
  /** The Waiting in line tile's count, or null before the read is in. */
  readonly waiting: () => number | null;
  /** A machine's display name by its runner id, or null when it is not in the read. */
  readonly machineName: (runnerId: string) => string | null;
};

export function WorkInProgressSummary(props: WorkInProgressSummaryProps): JSX.Element {
  const head = (): readonly TaskRow[] =>
    [...props.rows()].sort(byTaskRecency).slice(0, SUMMARY_ROWS);
  const count = (n: number | null): string =>
    n === null ? t('fleet.machines.not_reported') : String(n);
  const machine = (task: TaskRow): string =>
    (task.runner_id === null ? null : props.machineName(task.runner_id)) ??
    t('fleet.machines.not_reported');
  return (
    <Panel
      class="fleet-work-summary"
      title={t('fleet.work_summary_heading')}
      action={<A href={orgHref('/fleet/work')}>{t('fleet.work_summary_see_all')}</A>}
    >
      <ul class="fleet-work-summary__counts" aria-label={t('fleet.work_summary_counts_aria')}>
        <li>
          {t('fleet.band_working')}{' '}
          <span class="fleet-work-summary__num">{count(props.working())}</span>
        </li>
        <li>
          {t('fleet.band_waiting')}{' '}
          <span class="fleet-work-summary__num">{count(props.waiting())}</span>
        </li>
      </ul>
      <Show
        when={head().length > 0}
        fallback={<p class="fleet-work-summary__empty">{t('fleet.work_summary_empty')}</p>}
      >
        <ul class="fleet-work-summary__rows">
          <For each={head()}>
            {(task) => (
              <li class="fleet-work-summary__row" data-status={task.status}>
                <span class="fleet-work-summary__what">
                  <span class="fleet-work-summary__title">{task.title}</span>
                  <span class="fleet-work-summary__repo">{task.repo_full_name}</span>
                </span>
                <span class="fleet-work-summary__machine">{machine(task)}</span>
                <StatusPill tone={TASK_STATUS_TONE[task.status]}>
                  {t(taskWorkWord(task))}
                </StatusPill>
                <span class="fleet-work-summary__time">{ago(task.created_at)}</span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </Panel>
  );
}
